/**
 * Zoning thermique H1/H2/H3 (RT2012) et zones Anah (MaPrimeRénov')
 * par département français.
 *
 * Sources :
 *  - Arrêté du 26 octobre 2010 (RT2012) — zones climatiques H1/H2/H3
 *  - ANAH 2026 — plafonds de revenus pour MaPrimeRénov'
 */

export type ClimateZone = 'H1' | 'H2' | 'H3';
export type AnahZone = 'AB1' | 'B2C';

/**
 * Zones H1 : nord, Île-de-France, Alsace-Lorraine, Bourgogne-Franche-Comté,
 * Normandie partielle, Alpes du Nord.
 */
const H1_DEPTS = new Set([
  '02', '08', '10', '14', '21', '25', '39', '50', '51', '52',
  '54', '55', '57', '58', '59', '60', '61', '62', '67', '68',
  '70', '71', '73', '74', '75', '76', '77', '78', '80', '88',
  '89', '90', '91', '92', '93', '94', '95',
]);

/** Zones H3 : Méditerranée + Corse */
const H3_DEPTS = new Set([
  '06', '11', '13', '2A', '2B', '30', '34', '66', '83', '84',
]);

/**
 * Zone Anah A/B1 : grandes agglomérations (plafonds de revenus plus élevés).
 * Simplification par département — le zonage réel est par commune (PPPI).
 * En pratique ~95 % des communes sont en B2/C.
 */
const AB1_DEPTS = new Set([
  '75', '77', '78', '91', '92', '93', '94', '95', // Île-de-France
  '06', '13', '31', '33', '34', '38', '44', '59', '67', '69', '74', '76',
]);

export interface ZoningInfo {
  climateZone: ClimateZone;
  anahZone: AnahZone;
}

export function getZoning(codeDepartement: string): ZoningInfo {
  const dep = codeDepartement.length === 1 ? `0${codeDepartement}` : codeDepartement;
  const climateZone: ClimateZone = H1_DEPTS.has(dep)
    ? 'H1'
    : H3_DEPTS.has(dep)
      ? 'H3'
      : 'H2';
  const anahZone: AnahZone = AB1_DEPTS.has(dep) ? 'AB1' : 'B2C';
  return { climateZone, anahZone };
}

export const CLIMATE_ZONE_LABELS: Record<ClimateZone, string> = {
  H1: 'Zone H1 · Froide',
  H2: 'Zone H2 · Tempérée',
  H3: 'Zone H3 · Méditerranéenne',
};

export const CLIMATE_ZONE_COLORS: Record<ClimateZone, string> = {
  H1: 'bg-blue-100 text-blue-800',
  H2: 'bg-brand-100 text-brand-800',
  H3: 'bg-orange-100 text-orange-800',
};

export const ANAH_ZONE_LABELS: Record<AnahZone, string> = {
  AB1: 'A/B1 · Grande agglo.',
  B2C: 'B2/C · Zone courante',
};

export type RevenuBracket = 'tres-modestes' | 'modestes' | 'intermediaires' | 'superieurs';

/**
 * Plafonds de revenus fiscaux de référence Anah 2026 (ménage 1 personne).
 * Source : https://france-renov.gouv.fr/aides/mpr
 */
export const REVENUS_PLAFONDS: Record<
  AnahZone,
  { 'tres-modestes': number; modestes: number; intermediaires: number }
> = {
  B2C: {
    'tres-modestes': 22_461,
    modestes: 27_343,
    intermediaires: 38_184,
  },
  AB1: {
    'tres-modestes': 25_714,
    modestes: 31_098,
    intermediaires: 49_597,
  },
};

/**
 * Incréments par personne supplémentaire dans le foyer fiscal (au-delà de 1).
 * Source : barème Anah 2026 — https://france-renov.gouv.fr/aides/mpr
 */
export const REVENUS_INCREMENTS_PAR_PERSONNE: Record<
  AnahZone,
  { 'tres-modestes': number; modestes: number; intermediaires: number }
> = {
  B2C: {
    'tres-modestes': 5_094,
    modestes: 6_525,
    intermediaires: 9_256,
  },
  AB1: {
    'tres-modestes': 7_622,
    modestes: 9_296,
    intermediaires: 13_087,
  },
};

/**
 * Plafonds Anah ajustés pour la taille du foyer fiscal donné (1 personne ou +).
 */
export function getPlafondsForHousehold(
  anahZone: AnahZone,
  nbPersonnes: number,
): { 'tres-modestes': number; modestes: number; intermediaires: number } {
  const base = REVENUS_PLAFONDS[anahZone];
  const inc = REVENUS_INCREMENTS_PAR_PERSONNE[anahZone];
  const extra = Math.max(0, nbPersonnes - 1);
  return {
    'tres-modestes': base['tres-modestes'] + extra * inc['tres-modestes'],
    modestes: base.modestes + extra * inc.modestes,
    intermediaires: base.intermediaires + extra * inc.intermediaires,
  };
}

/**
 * Déduit la tranche de revenus Anah à partir du RFR, du nombre de personnes
 * dans le foyer fiscal et de la zone Anah (déduite de la commune).
 */
export function computeRevenuBracket(
  rfr: number,
  nbPersonnes: number,
  anahZone: AnahZone,
): RevenuBracket {
  const p = getPlafondsForHousehold(anahZone, nbPersonnes);
  if (rfr <= p['tres-modestes']) return 'tres-modestes';
  if (rfr <= p.modestes) return 'modestes';
  if (rfr <= p.intermediaires) return 'intermediaires';
  return 'superieurs';
}

/** Libellé court d'une tranche Anah. */
export const REVENU_BRACKET_LABELS: Record<RevenuBracket, string> = {
  'tres-modestes': 'Très modestes',
  modestes: 'Modestes',
  intermediaires: 'Intermédiaires',
  superieurs: 'Supérieurs',
};

/** Taux MaPrimeRénov’ propriétaire occupant en % par tranche (barème 2026). */
export const REVENU_BRACKET_MPR_TAUX: Record<RevenuBracket, number> = {
  'tres-modestes': 70,
  modestes: 60,
  intermediaires: 45,
  superieurs: 30,
};

/** Couleur du tag tranche pour le badge feedback. */
export const REVENU_BRACKET_COLORS: Record<RevenuBracket, string> = {
  'tres-modestes': 'bg-brand-100 text-brand-800',
  modestes: 'bg-brand-100 text-brand-700',
  intermediaires: 'bg-accent-100 text-accent-800',
  superieurs: 'bg-stone-100 text-stone-700',
};
